import { fieldValueDisplayFactory } from '@monorepo/shared/componentsV2/fieldDataType/values/fieldValueDisplayFactory';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { WysiwygContent } from '@monorepo/shared/componentsV2/WysiwygContent';
import { format } from 'date-fns';
import {
  DateLike,
  FieldDataType,
  FieldValueVersion,
  localEquivalentOfUTC,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';

const StyledTable = styled(Table)`
  padding-bottom: 0.5rem;
` as typeof Table;

const ActiveLabel = styled.span`
  font-style: italic;
`;

type FieldVersionRow = {
  id: string;
  displayedValue: React.ReactElement | string;
  startDate: string;
  endDate: React.ReactElement | string;
  notes: React.ReactElement | string;
};

const formatVersionDate = (date: DateLike | null) =>
  date ? format(localEquivalentOfUTC(new Date(date)), 'P') : '';

type FieldVersionHistoryModalProps = {
  fieldName: string;
  fieldType: FieldDataType;
  onClose: () => void;
  resourceName: string;
  versions: FieldValueVersion[];
};

export function FieldVersionHistoryModal({
  fieldName,
  fieldType,
  onClose,
  resourceName,
  versions,
}: FieldVersionHistoryModalProps) {
  const rows = useMemo(
    () =>
      versions
        .map((version, index) => ({
          id: `${index}`,
          displayedValue: fieldValueDisplayFactory({
            fieldType,
            fieldValue: version,
          }),
          startDate: formatVersionDate(version.effectivePeriod.begin),
          endDate: version.effectivePeriod.end ? (
            formatVersionDate(version.effectivePeriod.end)
          ) : (
            <ActiveLabel>Active</ActiveLabel>
          ),
          notes: version.notes ? <WysiwygContent html={version.notes} /> : '',
        }))
        .reverse(),
    [fieldType, versions],
  );

  const columns: TableColumn<FieldVersionRow>[] = useMemo(
    () => [
      { id: 'value', header: fieldName, contents: 'displayedValue' },
      { id: 'startDate', header: 'Start date', contents: 'startDate' },
      { id: 'endDate', header: 'End date', contents: 'endDate' },
      { id: 'reference', header: 'Reference', contents: 'notes' },
    ],
    [fieldName],
  );

  return (
    <BaseModal
      maxWidth="md"
      onClose={onClose}
      open
      showCloseButton
      title={`${fieldName} history for ${resourceName}`}
      unpaddedContent
    >
      <StyledTable
        ariaLabel={`${fieldName} version history`}
        columns={columns}
        rows={rows}
      />
    </BaseModal>
  );
}
